import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft,
  Users,
  MagnifyingGlass,
  ChatCircleDots,
  CircleNotch,
  Phone,
  IdentificationCard,
  CaretLeft,
  CaretRight,
  ClockClockwise,
} from "@phosphor-icons/react";

/**
 * Customer Directory — every contact the inbox has ever seen.
 *
 * Per user rule 2026-02-15:
 *  • Paginated all-customers list (NOT time-windowed like /api/contacts)
 *  • Server-side search across phone / WhatsApp name / RodBez name
 *  • Each row shows phone, name, RodBez customer ID (if linked),
 *    last activity time
 *  • A chat icon button on each row links straight to the inbox
 *    pre-selected on that contact: navigate(`/?phone=${phone}`)
 */
function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

const PAGE_SIZE = 50;

export default function Customers() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  // Debounce search so we don't spam the API on every keystroke.
  const debounceRef = useRef(null);
  const [debouncedQuery, setDebouncedQuery] = useState("");
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedQuery(query.trim());
      setPage(1);  // reset to first page on every new search
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        page_size: String(PAGE_SIZE),
      });
      if (debouncedQuery && debouncedQuery.length >= 2) {
        params.set("q", debouncedQuery);
      }
      const r = await api.get(`/customers/directory?${params}`);
      setRows(Array.isArray(r.data?.customers) ? r.data.customers : []);
      setTotal(r.data?.total || 0);
      setHasMore(!!r.data?.has_more);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load customers");
    } finally {
      setLoading(false);
    }
  }, [page, debouncedQuery]);

  useEffect(() => {
    load();
  }, [load]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / PAGE_SIZE)),
    [total],
  );

  const openChat = (phone) => {
    if (!phone) return;
    navigate(`/?phone=${encodeURIComponent(phone)}`);
  };

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--fg)]">
      {/* Header */}
      <div className="sticky top-0 z-20 border-b border-[var(--border)] bg-[var(--surface)]/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-2">
          <button
            onClick={() => navigate("/")}
            className="p-2 rounded-md hover:bg-[var(--bg)] transition-colors"
            title="Back to inbox"
            data-testid="customers-back-btn"
          >
            <ArrowLeft size={18} />
          </button>
          <div className="flex items-center gap-2">
            <Users size={20} weight="duotone" className="text-[#25D366]" />
            <h1 className="text-lg font-semibold">Customer Directory</h1>
            <span
              className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#25D366]/15 text-[#15803D] dark:text-[#86EFAC] border border-[#25D366]/30"
              data-testid="customers-total"
            >
              {total.toLocaleString("en-IN")} total
            </span>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={load}
              className="p-2 rounded-md hover:bg-[var(--bg)] transition-colors"
              title="Refresh"
              data-testid="customers-refresh-btn"
            >
              <ClockClockwise size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-7xl mx-auto px-4 py-5 space-y-4">
        {/* Search */}
        <div className="relative">
          <MagnifyingGlass
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)]"
          />
          <input
            type="text"
            placeholder="Search by name, phone or RodBez customer name (min 2 chars)"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full pl-10 pr-3 py-2.5 rounded-md bg-[var(--surface)] border border-[var(--border)] text-sm focus:outline-none focus:border-[#25D366]/60"
            data-testid="customers-search-input"
          />
        </div>

        {/* List */}
        {loading ? (
          <div className="py-16 text-center text-[var(--muted)]">
            <CircleNotch size={22} className="inline-block animate-spin" />
          </div>
        ) : rows.length === 0 ? (
          <div
            className="py-16 text-center text-[var(--muted)] text-sm"
            data-testid="customers-empty-state"
          >
            {debouncedQuery
              ? `No customers match "${debouncedQuery}"`
              : "No customers yet"}
          </div>
        ) : (
          <div className="rounded-xl border border-[var(--border)] bg-[var(--surface)] overflow-hidden">
            {/* Mobile cards (below md) */}
            <div className="md:hidden divide-y divide-[var(--border)]">
              {rows.map((c) => (
                <div
                  key={c.phone}
                  className="p-3 flex items-center gap-3"
                  data-testid={`customer-card-${c.phone}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">
                      {c.rodbez_customer_name || c.name || c.phone}
                    </div>
                    <div className="text-xs text-[var(--muted)] font-mono flex items-center gap-1">
                      <Phone size={11} /> +{c.phone}
                    </div>
                    {c.rodbez_customer_id && (
                      <div className="text-[10px] text-[#15803D] dark:text-[#86EFAC] font-mono mt-0.5">
                        <IdentificationCard size={10} className="inline" />{" "}
                        {c.rodbez_customer_id}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => openChat(c.phone)}
                    className="p-2 rounded-md bg-[#25D366]/15 hover:bg-[#25D366]/25 text-[#15803D] dark:text-[#86EFAC] shrink-0"
                    title="Open chat"
                    data-testid={`customer-chat-${c.phone}`}
                  >
                    <ChatCircleDots size={18} weight="fill" />
                  </button>
                </div>
              ))}
            </div>

            {/* Desktop table */}
            <table className="hidden md:table w-full text-sm" data-testid="customers-table">
              <thead className="text-[11px] uppercase tracking-wider text-[var(--muted)] bg-[var(--bg)]">
                <tr>
                  <th className="text-left px-4 py-2.5 font-medium">Name</th>
                  <th className="text-left px-4 py-2.5 font-medium">Phone</th>
                  <th className="text-left px-4 py-2.5 font-medium">RodBez Customer ID</th>
                  <th className="text-left px-4 py-2.5 font-medium hidden lg:table-cell">
                    Last activity
                  </th>
                  <th className="text-right px-4 py-2.5 font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr
                    key={c.phone}
                    className="border-t border-[var(--border)] hover:bg-[var(--bg)]/60 transition-colors"
                    data-testid={`customer-row-${c.phone}`}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium truncate max-w-[220px]">
                        {c.rodbez_customer_name || c.name || "—"}
                      </div>
                      {c.rodbez_customer_name && c.name && c.name !== c.rodbez_customer_name && (
                        <div className="text-[10px] text-[var(--muted)] truncate max-w-[220px]">
                          WA: {c.name}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono text-[var(--fg)]">+{c.phone}</td>
                    <td className="px-4 py-3 font-mono text-xs">
                      {c.rodbez_customer_id ? (
                        <span
                          className="px-2 py-0.5 rounded bg-[#25D366]/10 text-[#15803D] dark:text-[#86EFAC] border border-[#25D366]/30"
                          title="Linked RodBez customer"
                        >
                          {c.rodbez_customer_id}
                        </span>
                      ) : (
                        <span className="text-[var(--muted)] italic">not linked</span>
                      )}
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell text-[var(--muted)] text-xs">
                      {fmtTime(c.last_timestamp)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => openChat(c.phone)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#25D366]/15 hover:bg-[#25D366]/25 text-[#15803D] dark:text-[#86EFAC] text-xs font-medium"
                        title="Open chat with this customer"
                        data-testid={`customer-chat-${c.phone}`}
                      >
                        <ChatCircleDots size={14} weight="fill" />
                        Chat
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!loading && rows.length > 0 && (
          <div className="flex items-center justify-between text-xs text-[var(--muted)]">
            <div data-testid="customers-page-info">
              Page {page} of {totalPages} · showing{" "}
              {((page - 1) * PAGE_SIZE) + 1}–
              {Math.min(page * PAGE_SIZE, total)} of {total.toLocaleString("en-IN")}
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-2 py-1.5 rounded border border-[var(--border)] disabled:opacity-40 hover:bg-[var(--bg)] flex items-center gap-1"
                data-testid="customers-prev-btn"
              >
                <CaretLeft size={12} /> Prev
              </button>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={!hasMore}
                className="px-2 py-1.5 rounded border border-[var(--border)] disabled:opacity-40 hover:bg-[var(--bg)] flex items-center gap-1"
                data-testid="customers-next-btn"
              >
                Next <CaretRight size={12} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
